import { setClock, VirtualClock } from '../Clock.ts';
import { BotController, DEFAULT_BOT_PARAMS, type BotParams } from '../controllers/BotController.ts';
import { SimulatedStimulationController } from '../controllers/SimulatedStimulationController.ts';
import type { StimulationControllerOptions } from '../controllers/StimulationController.ts';
import type { GameConfig } from '../game/GameState.ts';
import { SimulatedHumanStimulator, type HumanResponseModel } from '../stimulation/SimulatedHumanStimulator.ts';
import type { SafetyLimits } from '../stimulation/SafetyGate.ts';
import { VirtualButtonDevice } from '../hardware/VirtualButtonDevice.ts';
import { ExperimentSession } from './ExperimentSession.ts';
import { quantile, type LatencyStats } from './LatencyTracker.ts';

/**
 * One experimental condition: everything that differs between the arms of a
 * comparison. A condition plus a seed fully determines a trial.
 */
export interface TrialCondition {
  id: string;
  label: string;
  /**
   * `direct` -- the bot's decision is the flap, no actuator in between.
   * `mediated` -- the decision becomes a stimulation request, and only a
   *   detected button press from the simulated participant flaps the bird.
   */
  mode: 'direct' | 'mediated';
  /** Response model of the simulated participant. Ignored in direct mode. */
  human: Partial<HumanResponseModel>;
  bot: Partial<BotParams>;
  safety: Partial<SafetyLimits>;
  controller: Partial<StimulationControllerOptions>;
  /**
   * Latency the bot compensates for, ms. `null` means "use the condition's own
   * mean response latency", i.e. a perfectly calibrated bot.
   */
  assumedLatencyMs: number | null;
}

export interface TrialResult {
  conditionId: string;
  label: string;
  seed: number;

  score: number;
  survivalMs: number;
  survivedToEnd: boolean;
  flaps: number;

  /** Bot decisions that asked for a flap. */
  intents: number;
  stimRequested: number;
  stimRejected: number;
  /** Inputs detected and attributed to a request. */
  movements: number;
  /** Requests that timed out with no detected response. */
  missed: number;
  /** Inputs with no plausible parent request. */
  unattributed: number;

  /** movements / stimRequested. The headline number for "did it work?". */
  commandSuccessRate: number;
  /** Flaps actually executed per second of survival. */
  flapsPerSec: number;
  /** Stimulations requested per second of survival -- the participant's load. */
  stimPerSec: number;

  latencyMedianMs: number;
  latencyMeanMs: number;
  latencyP95Ms: number;
  latencyJitterMs: number;
}

export interface ConditionSummary {
  conditionId: string;
  label: string;
  trials: number;

  meanScore: number;
  maxScore: number;
  medianScore: number;
  meanSurvivalMs: number;
  medianSurvivalMs: number;
  deaths: number;

  meanIntents: number;
  meanStimRequested: number;
  meanStimRejected: number;
  commandSuccessRate: number;
  meanMissed: number;
  flapsPerSec: number;
  stimPerSec: number;

  latencyMedianMs: number;
  latencyMeanMs: number;
  latencyP95Ms: number;
  latencyJitterMs: number;
}

export interface TrialRunOptions {
  seeds: number[];
  /** Simulated seconds per trial. */
  durationSec: number;
  game: Partial<GameConfig>;
  /** Called after each trial, for progress reporting. */
  onProgress?: (done: number, total: number, last: TrialResult) => void;
  /** Yield to the browser between trials so the UI can repaint. */
  yieldBetweenTrials: boolean;
}

export const DEFAULT_TRIAL_OPTIONS: TrialRunOptions = {
  seeds: [1, 2, 3, 4, 5],
  durationSec: 60,
  game: {},
  yieldBetweenTrials: true,
};

/**
 * The standard comparison set from the study design.
 *
 * Keyboard is absent on purpose: it needs a human at the keyboard and cannot be
 * run headless. Record it as a live session instead and compare the exported
 * summary against these.
 */
export function standardConditions(): TrialCondition[] {
  const base = (id: string, label: string, over: Partial<TrialCondition>): TrialCondition => ({
    id, label, mode: 'mediated', human: {}, bot: {}, safety: {}, controller: {},
    assumedLatencyMs: null, ...over,
  });

  // The bot's own refractory period bounds how often it will ask; the safety
  // gate's bounds how often a person may be stimulated. For a purely synthetic
  // actuator the second one is relaxed so the delay itself is what is measured
  // rather than the rate limiter.
  const fastGate: Partial<SafetyLimits> = { minIntervalMs: 40, maxPerSecond: 25, maxPerMinute: 1500 };
  // A perfect actuator that is merely slow. `buttonHoldMs` is dropped to 20 so
  // that back-to-back requests are not swallowed by the contact still being
  // closed -- these conditions are meant to isolate delay, nothing else.
  const noNoise: Partial<HumanResponseModel> = {
    latencyStdDevMs: 0, successProbability: 1, falsePositiveProbability: 0,
    refractoryPeriodMs: 0, buttonHoldMs: 20,
  };

  return [
    base('direct', 'Direct bot (no actuator)', { mode: 'direct' }),
    base('delay50', 'Bot + 50 ms delay', {
      human: { ...noNoise, meanLatencyMs: 50 }, safety: fastGate,
    }),
    base('delay100', 'Bot + 100 ms delay', {
      human: { ...noNoise, meanLatencyMs: 100 }, safety: fastGate,
    }),
    base('delay150', 'Bot + 150 ms delay', {
      human: { ...noNoise, meanLatencyMs: 150 }, safety: fastGate,
    }),
    base('delay100-uncomp', 'Bot + 100 ms delay, NO compensation', {
      human: { ...noNoise, meanLatencyMs: 100 },
      safety: fastGate,
      bot: { latencyCompensation: false },
    }),
    base('human-sim', 'Bot + simulated human noise (80±20 ms, 95%)', {
      human: {
        meanLatencyMs: 80, latencyStdDevMs: 20, successProbability: 0.95,
        refractoryPeriodMs: 120, falsePositiveProbability: 0,
      },
      safety: fastGate,
    }),
    base('delay200', 'Bot + 200 ms delay', {
      human: { ...noNoise, meanLatencyMs: 200 }, safety: fastGate,
    }),
    base('human-sim-slow', 'Bot + slow noisy human (150±30 ms, 90%)', {
      human: {
        meanLatencyMs: 150, latencyStdDevMs: 30, successProbability: 0.9,
        refractoryPeriodMs: 150, falsePositiveProbability: 0,
      },
      safety: fastGate,
    }),
  ];
}

/**
 * Runs conditions headlessly, thousands of times faster than realtime.
 *
 * It drives the *real* `ExperimentSession`, the real `StimulationController`,
 * the real `SafetyGate`, and the real simulated participant -- the only thing
 * substituted is the clock. A result from here and a result from watching the
 * same condition play out on screen are the same measurement.
 *
 * ## Why this is async
 *
 * `Stimulator.requestStimulus` is a promise, because a real serial device is.
 * A synchronous loop would therefore never let the simulated participant
 * schedule its response -- the promise callbacks would all queue up behind the
 * loop and fire after it finished. So each tick drains the microtask queue.
 * `await null` does that without yielding to timers or rAF, which keeps the
 * virtual clock from being observed by anything else.
 *
 * **Do not run this concurrently with the live session.** The clock is a
 * module-level binding; `App` stops its animation loop before starting a batch.
 */
export class TrialRunner {
  async runTrial(
    condition: TrialCondition,
    seed: number,
    durationSec: number,
    game: Partial<GameConfig> = {},
  ): Promise<TrialResult> {
    const clock = new VirtualClock(1000);
    const restore = setClock(clock);
    try {
      return await this.runTrialInner(condition, seed, durationSec, game, clock);
    } finally {
      restore();
    }
  }

  private async runTrialInner(
    condition: TrialCondition,
    seed: number,
    durationSec: number,
    game: Partial<GameConfig>,
    clock: VirtualClock,
  ): Promise<TrialResult> {
    const humanModel: HumanResponseModel = {
      meanLatencyMs: 80, latencyStdDevMs: 20, minLatencyMs: 25, successProbability: 0.95,
      falsePositiveProbability: 0, refractoryPeriodMs: 120, buttonHoldMs: 60,
      // Derive the participant's seed from the trial seed so that the pipe
      // course and the response noise vary together but reproducibly.
      seed: (seed * 2654435761) >>> 0,
      ...condition.human,
    };

    const assumed =
      condition.assumedLatencyMs !== null
        ? condition.assumedLatencyMs
        : condition.mode === 'direct'
          ? 0
          : humanModel.meanLatencyMs;

    const botParams: Partial<BotParams> = {
      ...DEFAULT_BOT_PARAMS,
      latencyMs: assumed,
      // Forecast paths are for the canvas; generating them here would be the
      // single largest cost in a batch of a hundred trials.
      collectPath: false,
      ...condition.bot,
    };

    const session = new ExperimentSession({
      sessionId: `trial-${condition.id}-${seed}`,
      game: { ...game, seed },
      logger: { attachGameState: false },
      safety: condition.safety,
      stateSampleHz: 0,
    });

    const bot = new BotController(session.engine.config, botParams);

    let controller: SimulatedStimulationController | BotController;
    let stimController: SimulatedStimulationController | null = null;
    let human: SimulatedHumanStimulator | null = null;

    if (condition.mode === 'direct') {
      controller = bot;
    } else {
      const button = new VirtualButtonDevice(`trial-button-${seed}`);
      human = new SimulatedHumanStimulator(button, humanModel);
      await session.stimulation.setStimulator(human);
      await session.stimulation.connect();
      await session.stimulation.arm();
      stimController = new SimulatedStimulationController(
        bot, session.stimulation, button, human, session.logger,
        () => session.engine.currentTick,
        {
          // A timeout well past the response distribution, so "missed" means
          // missed rather than "arrived a bit late".
          responseTimeoutMs: Math.max(250, humanModel.meanLatencyMs * 2 + 150),
          ...condition.controller,
        },
      );
      controller = stimController;
      session.registerPump((nowMs) => human?.pump(nowMs));
    }

    await session.setController(controller);
    session.reset(seed);

    const dtMs = session.dtMs;
    const maxTicks = Math.ceil(durationSec * session.engine.config.simHz);

    for (let t = 0; t < maxTicks; t++) {
      clock.advance(dtMs);
      session.advance(clock.now());
      if (!session.engine.isAlive) break;
      // Drain promise chains: service -> stimulator -> response scheduling.
      // Three turns covers the deepest chain in the mediated path.
      if (stimController) {
        await null;
        await null;
        await null;
      }
    }

    session.end();

    const state = session.engine.getState();
    const counters = stimController?.getCounters();
    const total = session.latency.stats('total');

    const stimRequested = counters?.requested ?? 0;
    const movements = counters?.movements ?? 0;

    return {
      conditionId: condition.id,
      label: condition.label,
      seed,
      score: state.score,
      survivalMs: state.simTimeMs,
      survivedToEnd: state.alive,
      flaps: session.engine.totalFlaps,
      intents: counters?.intents ?? session.engine.totalFlaps,
      stimRequested,
      stimRejected: counters?.rejected ?? 0,
      movements,
      missed: counters?.missed ?? 0,
      unattributed: counters?.unattributed ?? 0,
      commandSuccessRate: stimRequested > 0 ? movements / stimRequested : NaN,
      flapsPerSec: state.simTimeMs > 0 ? session.engine.totalFlaps / (state.simTimeMs / 1000) : NaN,
      stimPerSec: state.simTimeMs > 0 ? stimRequested / (state.simTimeMs / 1000) : NaN,
      latencyMedianMs: total.median,
      latencyMeanMs: total.mean,
      latencyP95Ms: total.p95,
      latencyJitterMs: total.jitter,
    };
  }

  /** Run every condition over every seed. */
  async run(
    conditions: TrialCondition[],
    options: Partial<TrialRunOptions> = {},
  ): Promise<{ results: TrialResult[]; summaries: ConditionSummary[] }> {
    const opts = { ...DEFAULT_TRIAL_OPTIONS, ...options };
    const results: TrialResult[] = [];
    const total = conditions.length * opts.seeds.length;

    for (const condition of conditions) {
      for (const seed of opts.seeds) {
        const result = await this.runTrial(condition, seed, opts.durationSec, opts.game);
        results.push(result);
        opts.onProgress?.(results.length, total, result);
        if (opts.yieldBetweenTrials) {
          await new Promise((r) => setTimeout(r, 0));
        }
      }
    }

    return { results, summaries: summarise(results, conditions, opts.durationSec) };
  }
}

export function summarise(
  results: readonly TrialResult[],
  conditions: readonly TrialCondition[],
  durationSec: number,
): ConditionSummary[] {
  const byCondition = new Map<string, TrialResult[]>();
  for (const r of results) {
    const list = byCondition.get(r.conditionId) ?? [];
    list.push(r);
    byCondition.set(r.conditionId, list);
  }

  const order = conditions.map((c) => c.id);
  return [...byCondition.entries()]
    .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
    .map(([id, list]) => {
      const scores = list.map((r) => r.score).sort((a, b) => a - b);
      const survivals = list.map((r) => r.survivalMs).sort((a, b) => a - b);
      const requested = sum(list.map((r) => r.stimRequested));
      const movements = sum(list.map((r) => r.movements));
      return {
        conditionId: id,
        label: list[0].label,
        trials: list.length,
        meanScore: mean(list.map((r) => r.score)),
        maxScore: Math.max(...scores),
        medianScore: quantile(scores, 0.5),
        meanSurvivalMs: mean(list.map((r) => r.survivalMs)),
        medianSurvivalMs: quantile(survivals, 0.5),
        deaths: list.filter((r) => !r.survivedToEnd).length,
        meanIntents: mean(list.map((r) => r.intents)),
        meanStimRequested: mean(list.map((r) => r.stimRequested)),
        meanStimRejected: mean(list.map((r) => r.stimRejected)),
        commandSuccessRate: requested > 0 ? movements / requested : NaN,
        meanMissed: mean(list.map((r) => r.missed)),
        flapsPerSec: mean(list.map((r) => r.flapsPerSec).filter(Number.isFinite)),
        stimPerSec: mean(list.map((r) => r.stimPerSec).filter(Number.isFinite)),
        latencyMedianMs: mean(list.map((r) => r.latencyMedianMs).filter(Number.isFinite)),
        latencyMeanMs: mean(list.map((r) => r.latencyMeanMs).filter(Number.isFinite)),
        latencyP95Ms: mean(list.map((r) => r.latencyP95Ms).filter(Number.isFinite)),
        latencyJitterMs: mean(list.map((r) => r.latencyJitterMs).filter(Number.isFinite)),
        // durationSec is carried through so a reader of the CSV can tell
        // "survived 60 s" from "survived 60 s out of 600".
        ...({ durationSec } as Record<string, number>),
      } as ConditionSummary;
    });
}

export function summariesToCSV(summaries: readonly ConditionSummary[]): string {
  const cols: (keyof ConditionSummary)[] = [
    'conditionId', 'label', 'trials', 'meanScore', 'maxScore', 'medianScore',
    'meanSurvivalMs', 'medianSurvivalMs', 'deaths', 'meanIntents', 'meanStimRequested',
    'meanStimRejected', 'commandSuccessRate', 'meanMissed', 'flapsPerSec', 'stimPerSec',
    'latencyMedianMs', 'latencyMeanMs', 'latencyP95Ms', 'latencyJitterMs',
  ];
  const rows = [cols.join(',')];
  for (const s of summaries) {
    rows.push(
      cols
        .map((c) => {
          const v = s[c];
          if (typeof v === 'number') return Number.isFinite(v) ? v.toFixed(3) : '';
          return /[",]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v);
        })
        .join(','),
    );
  }
  return rows.join('\n');
}

export function resultsToCSV(results: readonly TrialResult[]): string {
  const cols: (keyof TrialResult)[] = [
    'conditionId', 'label', 'seed', 'score', 'survivalMs', 'survivedToEnd', 'flaps',
    'intents', 'stimRequested', 'stimRejected', 'movements', 'missed', 'unattributed',
    'commandSuccessRate', 'flapsPerSec', 'stimPerSec', 'latencyMedianMs', 'latencyMeanMs', 'latencyP95Ms', 'latencyJitterMs',
  ];
  const rows = [cols.join(',')];
  for (const r of results) {
    rows.push(
      cols
        .map((c) => {
          const v = r[c];
          if (typeof v === 'number') return Number.isFinite(v) ? v.toFixed(3) : '';
          if (typeof v === 'boolean') return v ? '1' : '0';
          return /[",]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v);
        })
        .join(','),
    );
  }
  return rows.join('\n');
}

/** Kept for callers that want the raw stats object rather than the summary. */
export type { LatencyStats };

function sum(xs: number[]): number {
  return xs.reduce((a, b) => a + b, 0);
}

function mean(xs: number[]): number {
  return xs.length ? sum(xs) / xs.length : NaN;
}
